'use client';

import Link from 'next/link';
import paths from '@/paths';
import { usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { AmericanExpressCard, DiscoverCard, MasterCard, VisaCard } from '../icons';

export default function Footer() {
  const pathname = usePathname();
  const { data: session } = useSession();
  if (pathname.includes('/add-new')) return null;
  if (pathname.includes('/draft')) return null;
  if (pathname.includes('/register-tour-operater')) return null;
  if (pathname.includes('/list-your-tour')) return null;
  if (pathname.includes('/account')) return null;
  if (pathname.includes('/profile')) return null;
  if (pathname.includes('/reservation')) return null;

  const isAdsLandingPage =
    pathname === '/tiger-safari-tours' ||
    pathname === '/tiger-safari-planning' ||
    pathname === '/polar-bear-tours';
  const footerTopMargin = isAdsLandingPage ? 'mt-0' : 'mt-20';

  return (
    <footer className="w-full">
      <div
        className={`main-container mx-auto h-100 min-h-20 flex flex-col gap-6 justify-between border-t lg:border-none border-gray-800 pt-10 lg:pt-[160px] px-4 lg:px-0 ${footerTopMargin} lg:mt-0 ${pathname.includes('/contact-us') ? 'pb-[120px]' : 'pb-6 lg:pb-[120px]'}`}
      >
        {/* SECTION 1 — TOP ROW */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 lg:gap-10">
          <div className="flex flex-col gap-2">
            <h2 className="font-rokkitt text-xl lg:text-2xl font-semibold">FaunaExplore</h2>
            <p className="font-inter font-light text-sm leading-5 tracking-wide max-w-sm lg:max-w-none">
             Discover and book extraordinary wildlife tours with trusted operators worldwide.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 lg:gap-6 w-full lg:w-auto">
            <p className="font-inter font-light text-sm leading-5 tracking-wide">Supported payment methods</p>
            <div className="flex gap-4">
              <MasterCard />
              <VisaCard />
              <DiscoverCard />
              <AmericanExpressCard />
            </div>
          </div>
        </div>

        <div className="footer-separator border-t border-dotted border-white"></div>

        {/* SECTION 2 — FOOTER LINK COLUMNS */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-10">
          {/* Column 1 — Destinations */}
          <nav>
            <h3 className="text-md font-semibold font-rokkitt mb-4">Destinations</h3>
            <ul className="flex flex-col gap-2 list-none">
              <li className="mt-1 text-xs font-medium uppercase tracking-wide text-white/60">Africa</li>
              <li>
                <Link href="/destinations/kenya" className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  Kenya
                </Link>
              </li>
              <li>
                <Link href="/destinations/tanzania" className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  Tanzania
                </Link>
              </li>
              <li>
                <Link href="/destinations/south-africa" className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  South Africa
                </Link>
              </li>
              <li>
                <Link href="/destinations/namibia" className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  Namibia
                </Link>
              </li>
              <li>
                <Link href="/destinations/uganda" className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  Uganda
                </Link>
              </li>
              <li>
                <Link href="/destinations/rwanda" className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  Rwanda
                </Link>
              </li>
              <li className="mt-4 text-xs font-medium uppercase tracking-wide text-white/60">Asia</li>
              <li>
                <Link href="/destinations/india" className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  India
                </Link>
              </li>
              <li>
                <Link href="/destinations/indonesia" className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  Indonesia
                </Link>
              </li>
              <li className="mt-4 text-xs font-medium uppercase tracking-wide text-white/60">South America</li>
              <li>
                <Link href="/destinations/brazil" className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  Brazil
                </Link>
              </li>
              <li>
                <Link href="/destinations/chile" className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  Chile
                </Link>
              </li>
              <li className="mt-4 text-xs font-medium uppercase tracking-wide text-white/60">Europe</li>
              <li>
                <Link href="/destinations/norway" className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  Norway
                </Link>
              </li>
            </ul>
          </nav>

          {/* Column 2 — Experiences */}
          <nav aria-label="Experiences">
            <h3 className="text-md font-semibold font-rokkitt mb-4">Experiences</h3>
            <ul className="flex flex-col gap-2 list-none">
              <li>
                <Link href={paths.tours()} className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  All Tours
                </Link>
              </li>
              <li>
                <Link
                  href={paths.experience('gorilla-trekking')}
                  className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors"
                >
                  Gorilla Trekking
                </Link>
              </li>
              <li>
                <Link
                  href={paths.experience('jaguar-safari')}
                  className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors"
                >
                  Jaguar Safari
                </Link>
              </li>
              <li>
                <Link
                  href={paths.experience('black-leopard-safari')}
                  className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors"
                >
                  Black Leopard Safari
                </Link>
              </li>
              <li>
                <Link
                  href={paths.experience('snow-leopard-safari')}
                  className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors"
                >
                  Snow Leopard Safari
                </Link>
              </li>
              <li>
                <Link
                  href={paths.experience('tiger-safari')}
                  className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors"
                >
                  Tiger Safari
                </Link>
              </li>
              <li>
                <Link
                  href={paths.experience('diving-safari')}
                  className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors"
                >
                  Diving Safari
                </Link>
              </li>
              <li>
                <Link
                  href={paths.experience('orca-expedition')}
                  className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors"
                >
                  Orca Expedition
                </Link>
              </li>
            </ul>
          </nav>

          {/* Column 3 — For Partners */}
          <nav>
            <h3 className="text-md font-semibold font-rokkitt mb-4">For Partners</h3>
            <ul className="flex flex-col gap-2 list-none">
              <li>
                <Link
                  href={session ? paths.listYourTour() : paths.register()}
                  className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors"
                >
                  List your tour
                </Link>
              </li>
            </ul>
          </nav>

          {/* Column 4 — Company */}
          <nav>
            <h3 className="text-md font-semibold font-rokkitt mb-4">Company</h3>
            <ul className="flex flex-col gap-2 list-none">
              <li>
                <Link href="/about-us" className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  About us
                </Link>
              </li>
              <li>
                <Link href={paths.blog()} className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  Blog
                </Link>
              </li>
              <li>
                <Link href="/contact" className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  Contact us
                </Link>
              </li>
            </ul>
          </nav>

          {/* Column 5 — Legal + Follow Us (below Privacy Policy) */}
          <nav className="flex flex-col">
            <h3 className="text-md font-semibold font-rokkitt mb-4">Legal</h3>
            <ul className="flex flex-col gap-2 list-none">
              <li>
                <Link href={paths.terms()} className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link href={paths.privacy()} className="font-inter font-light text-md py-1 hover:text-primary-400 transition-colors">
                  Privacy Policy
                </Link>
              </li>
            </ul>
            <div className="mt-6 pt-1">
              <h3 className="text-md font-semibold font-rokkitt mb-4">Follow Us</h3>
              <ul className="flex flex-col gap-2 list-none">
                <li>
                  <Link
                    href="https://www.instagram.com/fauna_explore_official/"
                    className="font-inter font-semibold text-md py-1 hover:text-primary-400 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Instagram
                  </Link>
                </li>
                <li>
                  <Link
                    href="https://www.facebook.com/profile.php?id=61577360540975"
                    className="font-inter font-semibold text-md py-1 hover:text-primary-400 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Facebook
                  </Link>
                </li>
                <li>
                  <Link
                    href="https://www.youtube.com/@Fauna-Explore"
                    className="font-inter font-semibold text-md py-1 hover:text-primary-400 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    YouTube
                  </Link>
                </li>
              </ul>
            </div>
          </nav>
        </div>

        <div className="footer-separator border-t border-dotted border-white"></div>

        {/* SECTION 3 — COPYRIGHT */}
        <div className="flex justify-start">
          <span className="font-inter font-light text-md">© 2026 FaunaExplore. All rights reserved.</span>
        </div>
      </div>
    </footer>
  );
}
